<template>
  <div class="budget-settings">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".budget-settings"
                @cancel="closeHelp">
      <edit-budgets-help :openSection="openHelpSection"></edit-budgets-help>
    </res-dialog>
    <v-tabs v-model="tab"
            align-with-title>
      <v-tabs-slider color="yellow"></v-tabs-slider>

      <template v-for="(step, stepNum) in steps">
        <v-tab :key="stepNum">
          {{ step.name }}
        </v-tab>
      </template>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <!-- General Settings -->
      <v-tab-item>
        <v-card class="mb-12"
                color="white"
                height="100%">

          <span class="step-title">{{steps[0].name}}</span>
          <br />
          <span class="banner" v-if="!Boolean(budgetIterationId)">You may change the settings below later from within 'Settings'.</span>
          <br />
          <v-row align-content="end">
            <v-col cols="9">
              <label>Version Description (Optional)</label>
              <res-text-field :loading="currentLoading" :readonly="readonly" v-model="budgetSettings.description"></res-text-field>
            </v-col>
            <v-col cols="3">
              <div class="help-controls step-down" @click="openHelp(0)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
          </v-row>

          <span class="banner mt-2" v-if="budgetSettings.status != 'Open'">The settings below cannot be changed because the budget is not in 'open' status.</span>
          <v-row>
            <v-col cols="6">
              <label>GL Account Template</label>
              <template-selector ref="templateSelector" :readonly="!canEdit" v-model="budgetSettings.template" :loading="currentLoading" :propertyId="budgetSettings.property.value"></template-selector>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>
                Estimate Future Historicals for GL Account
                <v-tooltip top max-width="500px" nudge-right="50">
                  <ul>
                    <li>When viewing your General Ledger historical numbers, all of the months may not have numbers because the year may not be complete.</li>
                    <li>For Example: If you are working on your 2022 budget in September of 2021, then there will be no historical numbers for September, October, November or December of 2021 because these months have not happened.</li>
                    <li>These choices will offer you three options to populate those missing future months.</li>
                  </ul>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="inline">
                      <v-icon>info</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </label>
              <res-select :readonly="!canEdit" class="estimate-future-historicals-selector" v-model="budgetSettings.estimateFutureHistoricalsOption" :items="estimateFutureHistoricalsOptions" :loading="currentLoading"></res-select>
            </v-col>
            <span v-if="this.budgetSettings.estimateFutureHistoricalsOption != 5" class="step-down pt-5">and start in</span>
            <v-col v-if="this.budgetSettings.estimateFutureHistoricalsOption != 5" cols="3" class="step-down pt-4">
              <res-select :readonly="!canEdit" class="estimate-future-historicals-month-selector" v-model="budgetSettings.estimateFutureHistoricalOptionMonthOption" :items="estimateFutureHistoricalOptionMonthOptions" :loading="currentLoading"></res-select>
            </v-col>
            <span v-if="this.budgetSettings.estimateFutureHistoricalsOption != 5" class="step-down pt-5 pl-1">month</span>
          </v-row>
          <v-row v-show=false>
            <v-col cols="6">
              <label>
                Estimate Future Historicals for Occupancy Workbooks
                <v-tooltip top max-width="500px" nudge-right="50">
                  <ul>
                    <li>When viewing your Occupancy workbook Historical numbers, all of the months may not have numbers because the year may not be complete.</li>
                    <li>For Example: If you are working on your 2022 budget in September of 2021, then there will be no historical numbers for September, October, November or December of 2021 because these months have not happened.</li>
                    <li>These choices will offer you two options to populate those missing future months.</li>
                  </ul>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="inline">
                      <v-icon>info</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </label>
              <res-select :readonly="!canEdit" class="estimate-future-historicals-selector" v-model="budgetSettings.estimateFutureHistoricalsOccupancyWorkbooksOption" :items="estimateFutureHistoricalsOccupancyWorkbooksOptions" :loading="currentLoading"></res-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>Approval Workflow</label>
              <res-select :readonly="!canEdit" v-model="budgetSettings.approvalWorkflowId" :loading="currentLoading" :items="approvalWorkflowOptions"></res-select>
            </v-col>
          </v-row>

          <br />
          <span class="banner" v-if="Boolean(budgetIterationId)">The settings below cannot be changed because the budget has already been created.</span>
          <br />
          <v-row align-content="end">
            <v-col cols="9">
              <label>Property</label>
              <property-selector ref="propertySelector" :readonly="Boolean(budgetIterationId)" v-model="budgetSettings.property" :loading="currentLoading" elementId="import-property-selector"></property-selector>
            </v-col>
            <v-col cols="3" class="pb-0">
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>Year</label>
              <fiscal-year-selector :readonly="Boolean(budgetIterationId)" v-model="budgetSettings.year" :propertyId="budgetSettings.property.value" :loading="currentLoading" elementId="import-year-selector"></fiscal-year-selector>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>
                Start Month
                <v-tooltip top max-width="500px" nudge-right="150">
                  <span>Start month defaults to the beginning of your accounting year. This can be changed. You can budget for any 12 month period desired.</span>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="inline">
                      <v-icon>info</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </label>
              <res-select :readonly="!IsStartMonthEditable" class="start-month-selector" v-model="budgetSettings.startMonth" :items="startMonthOptions" :loading="currentLoading" attach=".start-month-selector"  @input="startMonthChanged" :menuProps="{top: true,nudgeBottom: '50px'}"></res-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>Accounting Basis</label>
              <res-select :readonly="Boolean(budgetIterationId)" class="accounting-basis-selector" v-model="budgetSettings.accountingBasisId" :items="accountingBasiiOptions" :loading="currentLoading"></res-select>
            </v-col>
          </v-row>
          <clear-all-dialog v-if="showStartMonthChangeDialog"
                    :showDialog="showStartMonthChangeDialog"
                    altText="This will clear all data from the budget and add back any starting default numbers. Are you sure you want to do this?"
                    :maxWidth="700"
                    :setWidth="700"
                    @confirm="changeStartMonth"
                    @cancel="closeStartMonthChangeDialog">
          </clear-all-dialog>
        </v-card>

        <v-btn v-if="!budgetIterationId"
               color="primary"
               :disabled="currentLoading"
               @click="create">
          Create
        </v-btn>
        <v-btn v-if="budgetIterationId"
               color="primary"
               :disabled="currentLoading || readonly"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>

      <!-- Lease Calculations -->
      <v-tab-item>
        <v-card class="mb-12"
                color="white"
                height="100%"
                min-height="416px">

          <span class="step-title">{{steps[1].name}}</span>
          <br />
          <span class="banner" v-if="!Boolean(budgetIterationId)">You may change the settings below later from within 'Settings'.</span>
          <span class="banner" v-if="budgetSettings.status != 'Open'">The settings below cannot be changed because the budget is not in 'open' status.</span>
          <br />
          <v-row>
            <v-col cols="11">
              <label>New Lease Calculations</label>
              <res-select :readonly="!canEdit || budgetSettings.reforecastLocked" class="new-lease-calculations-selector" v-model="budgetSettings.newLeaseCalculationOption" :items="newLeaseCalculationOptions" :loading="currentLoading"></res-select>
            </v-col>
            <v-col cols="1">
              <div class="help-controls step-down" @click="openHelp(1)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="budgetSettings.newLeaseCalculationOption==1" class="pl-3 mt-0">
            <span class="pt-5">The discount will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.discountMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent.</span>
          </v-row>

          <v-row v-if="budgetSettings.newLeaseCalculationOption==3" class="pl-3 mt-0">
            <span class="pt-5">This recurring concession will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.newRecurringConcessionMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent and will use concession transaction category:</span>
            <v-col cols="12" class="pl-0 px-6 pt-0">
              <v-combobox :readonly="!canEdit || budgetSettings.reforecastLocked" ref="newRecurringConcessionCombo" v-model="budgetSettings.newRecurringConcessionTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <label>Renewal Pricing Calculations</label>
              <res-select :readonly="!canEdit || budgetSettings.reforecastLocked" class="renewal-pricing-calculations-selector" v-model="budgetSettings.renewalPricingCalculationOption" :items="renewalPricingCalculationOptions" :loading="currentLoading"></res-select>
            </v-col>
          </v-row>

          <v-row v-if="budgetSettings.renewalPricingCalculationOption==1" class="pl-3 mt-0">
            <span class="pt-5">This increase will be</span>
            <v-col cols="1">
              <res-text-field class="short" :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.increaseOverExistingRentAmount"></res-text-field>
            </v-col>
            <v-col cols="2">
              <res-select :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.increaseOverExistingRentDenomination" :items="denominationOptions"></res-select>
            </v-col>
            <span class="pt-5">over exising rent.</span>
            <v-col cols="2">
              <v-checkbox class="mx-2 ma-0 pb-2 res-checkbox" label="Capped at Market Rent" v-model="budgetSettings.capRentIncreaseAtMarketRent"></v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="budgetSettings.renewalPricingCalculationOption==3" class="pl-3 mt-0">
            <span class="pt-5">This recurring concession will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.renewalRecurringConcessionMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent and will use concession transaction category:</span>
            <v-col cols="12" class="pl-0 px-6 pt-0">
              <v-combobox :readonly="!canEdit || budgetSettings.reforecastLocked" ref="rewewalRecurringConcessionCombo" v-model="budgetSettings.renewalRecurringConcessionTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>
        </v-card>

        <v-btn v-if="!budgetIterationId"
               color="primary"
               :disabled="currentLoading"
               @click="create">
          Create
        </v-btn>
        <v-btn v-if="budgetIterationId"
               color="primary"
               :disabled="currentLoading || readonly"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>

      <!-- Other Concessions and Bad Debt -->
      <v-tab-item>
        <v-card class="mb-12"
                color="white"
                height="100%">

          <span class="step-title">{{steps[2].name}}</span>
          <br />
          <span class="banner" v-if="!Boolean(budgetIterationId)">You may change the settings below later from within 'Settings'.</span>
          <span class="banner" v-if="budgetSettings.status != 'Open'">The settings below cannot be changed because the budget is not in 'open' status.</span>
          <br />
          <v-row>
            <v-col cols="9" class="py-0">
              <label>Other Concessions (Optional)</label>
            </v-col>
            <v-col cols="3" class="py-0">
              <div class="help-controls" @click="openHelp(2)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
            <v-col cols="12" class="py-0">
              <span>Want to enter budget numbers for additional concessions?  If so, please select them below:</span>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-combobox :readonly="!canEdit || budgetSettings.reforecastLocked" ref="otherConcessionTransactionCategoriesCombo" color="resBlue darken-1" dense outlined class="py-0" multiple v-model="budgetSettings.otherConcessionTransactionCategories" :items="transactionCategories" flat></v-combobox>
            </v-col>
          </v-row>

          <v-row class="py-0 mt-0">
            <v-col cols="10" class="py-0">
              <label>Bad Debt</label>
              <res-select :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.badDebtOption" :items="badDebtOptions"></res-select>
            </v-col>
          </v-row>

          <v-row v-if="budgetSettings.badDebtOption==1" class="pl-3 py-0">
            <span class="pt-5">This will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit || budgetSettings.reforecastLocked" v-model="budgetSettings.badDebtGLPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of</span>
            <v-col cols="8">
              <v-combobox :readonly="!canEdit || budgetSettings.reforecastLocked" ref="glCombo" color="resBlue darken-1" v-model="budgetSettings.badDebtGLAccount" :items="glAccounts" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

          <br v-if="budgetSettings.badDebtOption!==1" />
          <v-row class="py-0 mt-0">
            <v-col cols="10" class="py-0">
              <span>Bad Debt Transaction Category is: </span>
            </v-col>
            <v-col cols="10" class="py-0">
              <v-combobox :readonly="!canEdit || budgetSettings.reforecastLocked" ref="badDebtCombo" color="resBlue darken-1" v-model="budgetSettings.badDebtTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

        </v-card>

        <v-btn v-if="!budgetIterationId"
               color="primary"
               :disabled="currentLoading"
               @click="create">
          Create
        </v-btn>
        <v-btn v-if="budgetIterationId"
               color="primary"
               :disabled="currentLoading || readonly"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>

      <!-- Beginning Vacancies -->
      <v-tab-item>
        <v-card class="mb-12"
                color="white"
                height="100%">

          <span class="step-title">{{steps[3].name}}</span>
          <br />
          <span class="banner" v-if="!Boolean(budgetIterationId)">You may change the settings below later from within 'Settings'.</span>
          <span class="banner" v-if="budgetSettings.status != 'Open'">The settings below cannot be changed because the budget is not in 'open' status.</span>
          <br />
          <v-row align-content="center">
            <v-col cols="4"></v-col>
            <v-col cols="4">
              <v-btn @click="manualLoadProjectedVacancies"
                     :color="canEdit ? 'resBlue darken-1' : 'resGrey darken-1'"
                     :disabled="budgetSettings.reforecastLocked"
                     class="white--text">Load Projected Vacancies as of {{projectedVacancyDateDisplay | moment('MM/DD/YYYY')}}</v-btn>
            </v-col>
            <v-col cols="3"></v-col>
            <v-col cols="1">
              <div class="help-controls step-down" @click="openHelp(3)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="py-0 my-0"></v-col>
            <v-col cols="4" class="py-0 my-0">This excludes Model/Admin/Down Units</v-col>
            <v-col cols="4" class="py-0 my-0"></v-col>
          </v-row>
          <v-row>
            <v-col cols="4"></v-col>
            <v-col cols="4">
              <table cellpadding="0" cellspacing="0">
                <thead>
                <th class="resGrey--border resGrey lighten-1">Unit Type</th>
                <th class="resGrey--border resGrey lighten-1"># Units</th>
                </thead>
                <tbody>
                  <tr :key="unitType.unitTypeId" v-for="unitType in budgetSettings.beginningVacancies">
                    <td class="resGrey--border resGrey lighten-1">{{unitType.name}}</td>
                    <td class="resGrey--border white">
                      <res-text-field v-model="unitType.beginningVacancies" :readonly="currentLoading || !canEdit || budgetSettings.reforecastLocked" :filter="(item, queryText, itemText) => isNaN(queryText) ? itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) !== -1 : itemText.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase())" :filled="currentLoading" @input="updateProjectionLastUpdatedDate"></res-text-field>
                    </td>
                  </tr>
                </tbody>
              </table>
              <span class="pr-12">Table last updated as of {{budgetSettings.projectionLastUpdatedDate | moment('MM/DD/YYYY')}}</span>
            </v-col>
            <v-col cols="4"></v-col>
          </v-row>
        </v-card>

        <v-btn v-if="budgetIterationId"
               color="primary"
               :disabled="currentLoading || readonly"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
  import ResSelect from '@/components/base/ResSelect.vue';
  import ResTextField from '@/components/base/ResTextField.vue';
  import TemplateSelector from '@/components/templates/TemplateSelector.vue';

  import PropertySelector from '@/components/properties/PropertySelector.vue';
  import FiscalYearSelector from '@/components/properties/FiscalYearSelector.vue';
  import IsNullOrWhitespaceMixin from '@/mixins/isNullOrWhitespace.js';

  import ResDialog from '@/components/base/ResDialog.vue';
  import EditBudgetsHelp from '@/components/help/EditBudgetsHelp.vue';
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog.vue"
  import HelpableMixin from '@/mixins/Helpable.js';
  import EditBudget from '@/mixins/EditBudget.js';

  import accountService from '@/services/account';
  import budgetService from '@/services/budget';
  import glAccountService from '@/services/gl-account';
  import unitTypeService from '@/services/unit-type';
  import workflowService from '@/services/workflow';

  import * as fiscalYearOperations from '@/utilities/fiscal-year-operations';

  import { mapActions } from 'vuex';
  import { mapGetters } from 'vuex';

  export default {
    props: {
      height: {
        type: Number
      },
      maxWidth: {
        type: Number
      },
      showDialog: {
        type: Boolean
      },
      width: {
        type: Number
      },
      budgetIterationId: {
        type: String,
        default: null
      }
    },
    mixins: [IsNullOrWhitespaceMixin, HelpableMixin, EditBudget],
    data() {
      return {
        tab: 0,
        currentStep: 1,
        steps: [
          {
            name: 'General Settings',
            isComplete: (budgetSettings) => { return Boolean(budgetSettings.template && budgetSettings.estimateFutureHistoricalsOption && budgetSettings.estimateFutureHistoricalOptionMonthOption && budgetSettings.estimateFutureHistoricalsOccupancyWorkbooksOption); }
          },
          {
            name: 'Lease Calculations',
            isComplete: (budgetSettings) => {
              return Boolean(budgetSettings.newLeaseCalculationOption > 0 &&
                (budgetSettings.newLeaseCalculationOption == 1 ? budgetSettings.discountMarketRentPercentage > 0 : true) &&
                (budgetSettings.newLeaseCalculationOption == 3 ? budgetSettings.newRecurringConcessionMarketRentPercentage && budgetSettings.newRecurringConcessionTransactionCategoryId : true))
                &&
                Boolean(budgetSettings.renewalPricingCalculationOption > 0 &&
                  (budgetSettings.renewalPricingCalculationOption == 1 ? budgetSettings.increaseOverExistingRentAmount > 0 && budgetSettings.increaseOverExistingRentDenomination > 0 : true) &&
                  (budgetSettings.renewalPricingCalculationOption == 3 ? budgetSettings.renewalRecurringConcessionMarketRentPercentage && budgetSettings.renewalRecurringConcessionMarketRentPercentage && budgetSettings.renewalRecurringConcessionTransactionCategoryId : true))
            }
          },
          {
            name: 'Other Concessions and Bad Debt',
            isComplete: (budgetSettings) => {
              return Boolean(budgetSettings.badDebtOption &&
                (budgetSettings.badDebtOption > 1 || budgetSettings.badDebtGLPercentage && budgetSettings.badDebtGLAccount) &&
                budgetSettings.badDebtTransactionCategoryId)
            }
          },
          {
            name: 'Beginning Vacancies',
            isComplete: (budgetSettings) => { return !budgetSettings }
          }
        ],
        transactionCategories: [],
        approvalWorkflowOptions: [],
        glAccounts: [],
        lastModifiedTimestamp: null,
        rawBudgetSettings: null,
        budgetSettings: {
          budgetId: '',
          status: 'Open',//consider it open for frontend editing purposes before it's created
          property: { text: '', value: '' },
          year: null,
          accountingBasisId: 1,
          startMonth: '',
          description: '',
          template: '',
          estimateFutureHistoricalsOption: 0,
          estimateFutureHistoricalOptionMonthOption: 0,
          estimateFutureHistoricalsOccupancyWorkbooksOption: 0,
          newLeaseCalculationOption: 1,
          renewalPricingCalculationOption: 1,
          newRecurringConcessionMarketRentPercentage: 0,
          renewalRecurringConcessionMarketRentPercentage: 0,
          newRecurringConcessionTransactionCategoryId: null,
          renewalRecurringConcessionTransactionCategoryId: null,
          otherConcessionTransactionCategories: [],
          badDebtOption: 1,
          badDebtTransactionCategoryId: null,
          beginningVacancies: [],
          approvalWorkflowId: null,
          projectionLastUpdatedDate: new Date(),
          capRentIncreaseAtMarketRent: true,
          totalVersionNumber: 0
        },
        showStartMonthChangeDialog: false,
        originalStartMonth: ''
      }
    },
    components: {
      ResSelect,
      ResTextField,
      ResDialog,
      EditBudgetsHelp,
      PropertySelector,
      TemplateSelector,
      FiscalYearSelector,
      ClearAllDialog
    },
    methods: {
      ...mapActions(['changeLoading']),
      getMonthOptions(year, includeYear, startMonth) {
        startMonth = startMonth || 0;
        let priorYear = includeYear ? year - 1 : '';
        if (priorYear > 0 || !includeYear) {
          var months = [
            { text: 'January ', value: 1 },
            { text: 'February ', value: 2 },
            { text: 'March ', value: 3 },
            { text: 'April ', value: 4 },
            { text: 'May ', value: 5 },
            { text: 'June ', value: 6 },
            { text: 'July ', value: 7 },
            { text: 'August ', value: 8 },
            { text: 'September ', value: 9 },
            { text: 'October ', value: 10 },
            { text: 'November ', value: 11 },
            { text: 'December ', value: 12 }
          ];

          var returnMonths = [];
          for (var i = startMonth; i < startMonth + 12; i++) {
            var thisMonth = months[i % 12];
            thisMonth.text += i < 12 && i > 0 ? priorYear : i < 24 ? year : year + 1;
            returnMonths.push(thisMonth)
          }
          return returnMonths;
        }
        return []
      },
      async getConcessionTransactionCategories() {
        var result = await accountService.getTransactionCategories('Credit', this.budgetSettings.property.value);

        this.transactionCategories = result
          .filter((cat) => { return !cat.isGLAccountGainToLeaseLedgerItem && !cat.isUnitStatusVacancyLossLedgerItem })
          .map((cat) => { return { text: cat.name, value: cat.ledgerItemTypeId }; })
          .sort((a, b) => (a && a.text || '').toLowerCase().localeCompare((b && b.text || '').toLowerCase()));
      },
      async loadAvailableWorkflows() {
        let workflowData = await workflowService.getDropdown();
        if (workflowData && workflowData.data) {
          let wfs = [];

          wfs.push({ text: 'No approval workflow selected', value: 0 });
          for (var i = 0; i < workflowData.data.length; i++) {
            wfs.push(workflowData.data[i]);
          }
          this.approvalWorkflowOptions = wfs;
        } else {
          let wfs = [];
          wfs.push({ text: 'No approval workflow selected', value: 0 });
          this.approvalWorkflowOptions = wfs;
        }
      },
      updateProjectionLastUpdatedDate() {
        this.budgetSettings.projectionLastUpdatedDate = new Date();
      },
      async manualLoadProjectedVacancies() {
        this.budgetSettings.projectionLastUpdatedDate = new Date();
        await this.loadProjectedVacancies();
      },
      async loadProjectedVacancies() {
        if (!this.projectedVacancyDate) {
          return;
        }
        this.changeLoading(true);
        let projectedVacancies = await unitTypeService.getProjectedVacancies(this.budgetSettings.property.value, this.projectedVacancyDate);
        (this.budgetSettings.beginningVacancies).forEach((item) => {
          let unitTypeVacancies = projectedVacancies.find(e => e.unitTypeId.toUpperCase() == item.unitTypeId.toUpperCase());
          if (unitTypeVacancies) {
            item.beginningVacancies = unitTypeVacancies.projectedVacancies;
          }
          else {
            item.beginningVacancies = 0;
          }
        });
        this.changeLoading(false);
      },
      async loadGlAccounts() {
        this.glAccounts = ((await glAccountService.getGlAccounts(null, 'Income')).concat(await glAccountService.getGlAccounts(null, 'Expense')))
          .sort((glA, glB) => { if (glA.number < glB.number) { return -1; } if (glA.number > glB.number) { return 1; } return 0; })
          .map((gl) => { return { text: gl.number + ' - ' + gl.name, value: gl.glAccountId }; });
      },
      async loadBudgetSettings() {
        if (!this.budgetIterationId) {
          return;
        }
        let settings = await budgetService.getBudgetSettings(this.budgetIterationId);
        this.status = settings.status;
        this.userCanEdit = settings.userCanEdit;
        this.userCanEditApproved = settings.userCanEditApproved;
        this.userCanEditPublished = settings.userCanEditPublished;
        this.budgetSettings = {
          budgetId: settings.budgetId,
          status: settings.status,
          property: { text: settings.propertyName, value: settings.propertyId },
          year: settings.year,
          accountingBasisId: settings.accountingBasisId,
          startMonth: settings.startMonth,
          description: settings.description,
          template: { text: settings.templateTitle, value: settings.templateId },
          increaseOverExistingRentAmount: settings.increaseOverExistingRentAmount,
          increaseOverExistingRentDenomination: settings.increaseOverExistingRentDenomination,
          capRentIncreaseAtMarketRent: settings.capRentIncreaseAtMarketRent,
          discountMarketRentPercentage: settings.discountMarketRentPercentage,
          estimateFutureHistoricalsOption: settings.estimateFutureHistoricalsOption,
          estimateFutureHistoricalOptionMonthOption: settings.estimateFutureHistoricalOptionMonthOption,
          estimateFutureHistoricalsOccupancyWorkbooksOption: settings.estimateFutureHistoricalsOccupancyWorkbooksOption,
          newLeaseCalculationOption: settings.newLeaseCalculationOption,
          renewalPricingCalculationOption: settings.renewalPricingCalculationOption,
          newRecurringConcessionMarketRentPercentage: settings.newRecurringConcessionMarketRentPercentage,
          renewalRecurringConcessionMarketRentPercentage: settings.renewalRecurringConcessionMarketRentPercentage,
          badDebtOption: settings.badDebtOption,
          badDebtGLPercentage: settings.badDebtGLPercentage,
          badDebtTransactionCategoryId: settings.badDebtTransactionCategoryId,
          projectionLastUpdatedDate: settings.projectionLastUpdatedDate,
          beginningVacancies: settings.beginningVacancies,
          reforecastLocked: settings.reforecastLocked,
          totalVersionNumber: settings.totalVersionNumber
        };
        this.originalStartMonth = settings.startMonth
        this.rawBudgetSettings = settings;
        this.updateControls(settings);
      },
      updateControls(budgetSettings) {
        if (!budgetSettings) {
          return;
        }
        setTimeout(() => {
          if (this.$refs.propertySelector) {
            this.$refs.propertySelector.selectItem(this.budgetSettings.property);
          }
          if (this.$refs.templateSelector) {
            this.$refs.templateSelector.selectItem(this.budgetSettings.template);
          }
          this.budgetSettings.approvalWorkflowId = budgetSettings.approvalWorkflowId || 0;
          if (this.transactionCategories.length > 0) {
            let fullOtherConcessionTransactionCategories = this.budgetSettings.otherConcessionTransactionCategories || [];
            this.transactionCategories.forEach((cat) => {
              if (cat.value == budgetSettings.badDebtTransactionCategoryId) {
                this.budgetSettings.badDebtTransactionCategoryId = cat;
                if (this.$refs.badDebtCombo) {
                  this.$refs.badDebtCombo.selectItem(cat);
                }
              }
              if (cat.value == budgetSettings.newRecurringConcessionTransactionCategoryId) {
                this.budgetSettings.newRecurringConcessionTransactionCategoryId = cat;
              }
              if (cat.value == budgetSettings.renewalRecurringConcessionTransactionCategoryId) {
                this.budgetSettings.renewalRecurringConcessionTransactionCategoryId = cat;
              }

              var index = budgetSettings.otherConcessionTransactionCategories.indexOf(cat.value);
              if (index !== -1) {
                fullOtherConcessionTransactionCategories.push(cat);
                budgetSettings.otherConcessionTransactionCategories.splice(index, 1)
              }
            })
            this.budgetSettings.otherConcessionTransactionCategories = fullOtherConcessionTransactionCategories;
          }
          if (this.glAccounts.length > 0) {
            this.glAccounts.forEach((gl) => {
              if (gl.value == budgetSettings.badDebtGLAccount) {
                this.budgetSettings.badDebtGLAccount = gl;
                if (this.$refs.glCombo) {
                  this.$refs.glCombo.selectItem(gl);
                }
              }
            });
          }
        }, 2500);
        this.lastModifiedTimestamp = this.budgetSettings.lastModifiedTimestamp = budgetSettings.lastModifiedTimestamp;
      },
      async create() {
        this.changeLoading(true);
        let succeeded = false;
        succeeded = await budgetService.createBudget(this.budgetSettings);

        this.$emit('save', succeeded);
        if (succeeded) {
          this.$eventHub.$emit('budgetSettingsUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Budget Settings Updated');
        }
        this.changeLoading(false);
      },
      async save() {
        this.changeLoading(true);
        let succeeded = false;

        succeeded = await budgetService.updateBudgetSettings(this.budgetIterationId, this.budgetSettings);

        this.$emit('save', succeeded);
        if (succeeded) {
          this.$eventHub.$emit('budgetSettingsUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Budget Settings Updated');
        }
        this.changeLoading(false);
      },
      cancel() {
        this.$emit('cancel', true)
      },
      startMonthChanged () {         
          if (this.budgetSettings.startMonth !== this.originalStartMonth){
              this.showStartMonthChangeDialog= true;
          }         
      },
      closeStartMonthChangeDialog(){
        this.budgetSettings.startMonth = this.originalStartMonth;
        this.showStartMonthChangeDialog= false;
      },
      async changeStartMonth(){
        this.showStartMonthChangeDialog= false;
        this.originalStartMonth = this.budgetSettings.startMonth;
        this.changeLoading(true);
        let succeeded = false;

        succeeded = await budgetService.changeStartMonth(this.budgetIterationId, this.budgetSettings);

        this.$emit('save', succeeded);
        if (succeeded) {
          this.$eventHub.$emit('budgetSettingsUpdated', this.budgetIterationId);
          this.$eventHub.$emit('showAlertNotification', 'Budget Settings Updated');
        }
        this.changeLoading(false);
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      startMonthOptions() {
        return this.getMonthOptions(this.budgetSettings.year, true)
      },
      accountingBasiiOptions() {
        return [
          { text: 'Accrual', value: 1 },
          { text: 'Cash', value: 2 }
        ]
      },
      estimateFutureHistoricalsOptions() {
        var currentYear = this.budgetSettings.startMonth == 1 ? this.budgetSettings.year - 1 : this.budgetSettings.year - 1;
        return [
          { text: `Estimate Future Historicals by annualizing current year (${currentYear}) actuals`, value: 1 },
          { text: `Estimate Future Historicals by using prior year (${currentYear - 1}) actuals`, value: 2 },
          { text: `Estimate Future Historicals using current year (${currentYear}) budget`, value: 3 },
          { text: `Estimate Future Historicals by using current year (${currentYear}) reforecast`, value: 4 },
          { text: `Do not Estimate Future Historicals months`, value: 5 },
        ]
      },
      estimateFutureHistoricalOptionMonthOptions() {
        var currentYear = this.budgetSettings.startMonth == 1 ? this.budgetSettings.year - 1 : this.budgetSettings.year - 1;
        var startMonth = this.budgetSettings.startMonth - 1 + 12;
        switch (this.budgetSettings.estimateFutureHistoricalsOption) {
          case 1:
            return this.getMonthOptions(currentYear, true, startMonth);
          case 2:
            return this.getMonthOptions(currentYear - 1, true, startMonth);
          case 3:
            return this.getMonthOptions(currentYear, true, startMonth);
          case 4:
            return this.getMonthOptions(currentYear, true, startMonth);
          case 5:
            return this.getMonthOptions(currentYear, true, startMonth);
        }
        return [];
      },
      estimateFutureHistoricalsOccupancyWorkbooksOptions() {
        var currentYear = this.budgetSettings.startMonth == 1 ? this.budgetSettings.year - 1 : this.budgetSettings.year - 1;
        return [
          { text: `Estimate Future Historicals by annualizing current year (${currentYear}) actuals`, value: 1 },
          { text: `Estimate Future Historicals by using prior year (${currentYear - 1}) actuals`, value: 2 },
          { text: `Do not Estimate Future Historical months`, value: 3 },
        ]
      },
      newLeaseCalculationOptions() {
        return [
          { text: 'New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)', value: 1 },
          { text: 'New Leases will be at Market Rent (no Gain/Loss to Lease)', value: 2 },
          { text: 'New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)', value: 3 }
        ]
      },
      renewalPricingCalculationOptions() {
        return [
          { text: 'Renewals will be given an increase over existing Rent', value: 1 },
          { text: 'Renewals will be at Market Rent', value: 2 },
          { text: 'Renewals will be at Market Rent AND also offer a recurring concession', value: 3 }
        ]
      },
      badDebtOptions() {
        return [
          { text: 'Bad Debt will be a percentage of the selected GL Account', value: 1 },
          { text: 'Bad Debt will be Average Market Rent multiplied by the number of skips/evictions', value: 2 },
          { text: 'Manual Entry', value: 3 }
        ]
      },
      projectedVacancyDate() {
        if (this.budgetSettings.startMonth > 0 && this.budgetSettings.year > 0) {
          return fiscalYearOperations.getEndOfPriorFiscalYear(this.budgetSettings.startMonth, this.budgetSettings.year);
        }
        return null;
      },
      projectedVacancyDateDisplay() {
        if (this.budgetSettings.startMonth > 0 && this.budgetSettings.year > 0) {
          let adjustedYear =  this.budgetSettings.startMonth>1?this.budgetSettings.year - 1:this.budgetSettings.year;
          return fiscalYearOperations.getEndOfPriorFiscalYear(this.budgetSettings.startMonth, adjustedYear);
        }
        return null;
      },
      denominationOptions() {
        return [
          { text: 'dollars ($)', value: 1 },
          { text: 'percent (%)', value: 2 }
        ];
      },
      canEdit() {
        return !this.readonly;
      },
      IsStartMonthEditable(){
        return true;// !(this.budgetIterationId) || (this.budgetSettings.status == 'Open' && this.budgetSettings.totalVersionNumber == 1);
      }
    },
    mounted() {
      this.getConcessionTransactionCategories();
      this.loadAvailableWorkflows();
      this.loadGlAccounts();
      this.loadBudgetSettings();
    },
    watch: {
      'tab': function () {
        if (this.rawBudgetSettings) {
          this.updateControls(this.rawBudgetSettings);
        }
      },
      'budgetSettings.property.value': function () {
        this.getConcessionTransactionCategories();
      }
    }
  };
</script>

<style lang="scss" scoped>
  .v-stepper,
  .v-stepper__header,
  .v-card.v-sheet {
    box-shadow: none;
  }

  .inline {
    display: inline !important;
  }

  .step-title {
    text-align: center;
    font-size: 24px;
    padding-bottom: 0;
    font-weight: bold;
    display: block;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .step-down {
    position: relative;
    top: 18px;
  }

  .resGrey--border {
    border: 1px solid var(--v-resGrey-darken1) !important;
    padding: 8px;
  }

  .help-controls {
    float: right;
    border-radius: 5px;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer !important;
  }
</style>

<style>
  div[role="combobox"] .v-text-field.v-text-field--solo .v-input__control {
    min-height: 20px;
  }

  div[role="combobox"] .v-input__append-inner {
    background-color: var(--v-resBlue-darken1);
    margin-top: 0 !important;
    margin-right: 0;
    position: absolute;
    right: -13px;
    border-bottom-right-radius: 5px;
    border-top-right-radius: 5px;
  }

  div[role="combobox"] .v-input__append-inner {
    padding-bottom: 7px !important;
  }

  div[role="combobox"] .v-input__icon--append {
    margin-top: -3px;
  }

  div[role="combobox"] .v-input__append-inner .v-icon {
    color: white !important;
  }

  .v-input--is-readonly div[role="combobox"],
  .v-input--is-readonly input[readonly="readonly"],
  .v-input--is-readonly .v-select__slot {
    opacity: 0.65;
  }

  div[role="combobox"] .v-input__append-inner {
    height: 40px !important;
  }

  div[role="combobox"] .v-input__icon.v-input__icon--append {
    margin-top: 8px;
    margin-left: 3px;
    padding-right: 5px;
  }
</style>
